import React, { useState, useRef, useEffect } from 'react'
import { Info } from '@mui/icons-material'
import { useMediaQuery } from 'react-responsive'
import { ClassNameValue } from 'tailwind-merge'
import cn from '../../../lib/utils/cn'
import { IconButton } from '../../../components/ui'
import { IconButtonProps } from '../../../components/ui/IconButton'

interface ContactInfoListProps {
	className?: ClassNameValue
	buttonConfig?: IconButtonProps
	contacts: { type: string; value: string }[]
	filterWindowClassName?: ClassNameValue
	onEmailClick?: (email: string) => void
}

interface Contact {
	type: string | number
	value: string
}

type ContactTypeFn = (contacts: Contact[], index: number) => string | undefined

const ContactInfoList: React.FC<ContactInfoListProps> = ({
	className,
	contacts,
	filterWindowClassName,
	onEmailClick,
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement
			if (
				containerRef.current?.contains(target) ||
				target?.closest('.select-dropdown-container') ||
				target?.closest('.select-dropdown-option')
			) {
				return
			}
			setIsOpen(false)
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const isDesktop = useMediaQuery({ minWidth: 1220 })

	const contactType: ContactTypeFn = (contacts, index) => {
		if (contacts[index]?.type === 0) {
			return 'Work'
		} else if (contacts[index]?.type === 1) {
			return 'Personal'
		}
	}

	const isEmail = (value: string) => {
		return value.includes('@')
	}

	const handleContactClick = (value: string) => {
		if (isEmail(value) && onEmailClick) {
			onEmailClick(value)
		}
	}

	return (
		<div
			className={cn(
				'group inline-block justify-center hover:bg-inherit dark:hover:bg-inherit',
				className
			)}
			onClick={(e) => e?.stopPropagation()}
			ref={containerRef}
		>
			<button
				className={cn(
					'text-xs border border-transparent group-hover:bg-transparent bg-inherit dark:bg-inherit dark:group-hover:bg-background-dark text-text-table dark:text-text-table-dark hover:bg-transparent rounded-md ml-1'
				)}
				onClick={() => setIsOpen((prev) => !prev)}
			>
				{`(+${contacts?.length - 1 || 0})`}
			</button>
			{isOpen && (
				<div
					className={cn(
						'absolute mt-2 bg-white dark:bg-background-dark border border-gray-300 shadow-lg  rounded-md z-50'
					)}
				>
					{contacts?.map((item, index) => (
						<div
							key={index}
							className="z-50 flex text-[14px] flex-col p-3 last:border-b-0 border-b border-stroke-light dark:border-stroke-light-dark"
						>
							<span className="text-sm font-medium dark:text-text-table-dark">
								{contactType(contacts, index)}:{' '}
							</span>
							<span
								className={cn(
									'text-sm text-text-main dark:text-text-main-dark',
									isEmail(item.value) &&
										onEmailClick &&
										'cursor-pointer text-blue-400 hover:text-accent-hover transition-colors duration-200'
								)}
								onClick={() => {
									!!onEmailClick && handleContactClick(item.value)
								}}
							>
								{' ' + item.value || '-'}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default ContactInfoList
